import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Receipt } from 'lucide-react';
import { addUserClaim, addUserClaimAttachments, getCompanyClaimTypes, getMyClaims } from '../../../api/Payroll/claim';
import { getUserInfo } from '../../../api/General/account';
import { getApiErrorMessage } from '../../../utils/apiErrors';
import { UserClaimInput, UserClaimStatus, UserClaimView, CompanyClaimTypeView } from '../../../models/payroll';
import YearMonthPicker from '../../../components/yearMonthPicker';
import { CompanyCurrencyRate, Currency } from '../../../models/general';
import { GetCompanyRates } from '../../../api/General/currency';
import { getCompanyRules } from '../../../api/General/Company';
import { formatDate, formatNumber } from '../../../utils';
import { useFileUploadLimits } from '../../../hooks/useFileUploadLimits';
import CurrencyDropdown from '../../../components/currencyDropdown';
import Pagination from '../../../components/Pagination';
import Button from '../../../components/ui/Button';
import { useWriteAccess } from '../../../hooks/useWriteAccess';
import RestrictedWriteNotice from '../../../components/subscription/RestrictedWriteNotice';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../../../components/ui/Dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/Table';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import StatusBadge from '../../../components/ui/StatusBadge';
import { Spinner } from '../../../components/ui/Spinner';
import AppIcon from '../../../components/AppIcon';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import { PageLayout } from '../../../components/ui/PageLayout';

const PAGE_SIZE = 10;

// ---------------------------------------------------------------------------
// Month bucketing
//
// THE RULE: a claim belongs to the calendar month of its RequestDate read in the
// EMPLOYEE'S PROFILE timezone. That is the frame the server writes in — ClaimService
// .AddUserClaim stamps `RequestDate = TimeZoneInfo.ConvertTimeFromUtc(UtcNow, user.TimeZone)`,
// i.e. a wall clock in the user's configured zone, and Newtonsoft serialises that
// Unspecified-kind value with NO offset ("2026-07-31T21:07:00"). The browser's zone never
// enters into it, and neither does UTC.
//
// The old code got HALF of this right by accident: `new Date("2026-07-31T21:07:00")` is
// parsed as browser-local, which happens to preserve the wall-clock digits, so the ROW's
// month was fine. What was wrong was the reference — `new Date()` is *now in the browser's*
// zone. At browser-local 2026-08-01 00:07 with a UTC profile the page asked for August while
// the claim it had just written was stamped July, so a freshly submitted claim rendered as
// "No claims found". Both sides now read the same zone.
interface YearMonth { year: number; month: number } // month is 0-based, like Date.getMonth()

/** A serialized wall clock with no offset — the shape RequestDate arrives in. */
const NAIVE_LOCAL_ISO = /^(\d{4})-(\d{2})-(\d{2})(?:[T ][\d:.]*)?$/;

/** The calendar month an INSTANT falls in, read in `timeZone` (browser zone if unknown/invalid). */
function monthInTimeZone(instant: Date, timeZone?: string): YearMonth {
  if (timeZone) {
    try {
      const parts = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: '2-digit' })
        .formatToParts(instant);
      const year = Number(parts.find(p => p.type === 'year')?.value);
      const month = Number(parts.find(p => p.type === 'month')?.value);
      if (Number.isFinite(year) && Number.isFinite(month)) return { year, month: month - 1 };
    } catch {
      // Unknown/legacy timezone id (e.g. a Windows id on an ICU runtime) — fall through.
    }
  }
  return { year: instant.getFullYear(), month: instant.getMonth() };
}

/**
 * The month a claim belongs to. A no-offset string is already profile-zone wall clock, so its
 * digits are taken literally; anything carrying a Z/offset is a real instant and is converted
 * INTO the profile zone — never re-bucketed by whatever zone the browser happens to sit in.
 */
function monthOfClaim(value: string | Date | undefined | null, timeZone?: string): YearMonth | null {
  if (!value) return null;
  if (typeof value === 'string') {
    const m = NAIVE_LOCAL_ISO.exec(value.trim());
    if (m) return { year: Number(m[1]), month: Number(m[2]) - 1 };
  }
  const instant = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(instant.getTime())) return null;
  return monthInTimeZone(instant, timeZone);
}

/** "This month" as the picker wants it — the 1st of the current month IN THE PROFILE ZONE. */
function currentMonthDate(timeZone?: string): Date {
  const { year, month } = monthInTimeZone(new Date(), timeZone);
  return new Date(year, month, 1);
}

/**
 * The policy refusals the server can return on submit, mapped onto the localized strings that
 * already exist in common.json. The server's `description` is English-only, and dropping it (the
 * old `e?.message || 'Submission failed'` — an axios rejection carries no `message` here, so it
 * ALWAYS lost) left the user blocked with no reason and no remaining budget. Anything that does
 * not match a known shape is surfaced verbatim rather than swallowed.
 */
const SERVER_REFUSALS: ReadonlyArray<{ re: RegExp; key: string; param: string }> = [
  { re: /^Monthly limit exceeded\.\s*Remaining budget:\s*(.+)$/i, key: 'claims.exceedsMonthlyLimit', param: 'remaining' },
  { re: /^Annual limit exceeded\.\s*Remaining budget:\s*(.+)$/i, key: 'claims.exceedsAnnualLimit', param: 'remaining' },
  { re: /^Claim amount exceeds the maximum allowed \((.+)\)\s*$/i, key: 'claims.exceedsMaxAmount', param: 'max' },
];

interface FormState {
  claimId: string;
  amount: string;
  comments?: string;
  attachments: File[];
}

const MyClaims: React.FC = () => {
  const { t } = useTranslation();
  // CO-24 — /api/Claim is not on the RestrictedWriteAllowlist; claim submission 403s while restricted.
  const write = useWriteAccess();
  const company = useSelector((s: any) => s.user.company);
  const { toast, showToast, dismissToast } = useToast();
  const { validateFileSize } = useFileUploadLimits();

  // Data
  const [claims, setClaims] = useState<UserClaimView[]>([]);
  const [claimTypes, setClaimTypes] = useState<CompanyClaimTypeView[]>([]);
  const [currencyOptions, setCurrencyOptions] = useState<CompanyCurrencyRate[]>([]);
  const [, setBaseCurrency] = useState<Currency | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<any>(0);

  // Filters & pagination
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [yearMonth, setYearMonth] = useState<Date>(new Date());
  const [page, setPage] = useState(1);
  // The employee's configured timezone — the frame RequestDate is written in. Undefined until the
  // profile lands (and if it never does, every helper falls back to the browser's zone, i.e. exactly
  // the old behaviour). Deliberately NOT the company timezone: the server keys off user.TimeZone.
  const [profileTimeZone, setProfileTimeZone] = useState<string | undefined>(undefined);
  // A month the user picked by hand must never be yanked out from under them when the profile
  // timezone arrives a moment later.
  const monthPickedByUser = React.useRef(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [openClaimDialog, setOpenClaimDialog] = useState(false);

  // Form
  const [form, setForm] = useState<FormState>({ claimId: '', amount: '', comments: '', attachments: [] });
  const [formErrors, setFormErrors] = useState<{ amount?: string; claimId?: string }>({});

  // Fetch claim types (on demand when dialog opens)
  const loadClaimTypes = useCallback(async () => {
    if (claimTypes.length) return;
    try {
      const resp = await getCompanyClaimTypes();
      setClaimTypes(resp.payload || []);
    } catch (e) {
      console.error(e);
    }
  }, [claimTypes.length]);

  const loadData = useCallback(async (currencyId?: number) => {
    setLoading(true);
    try {
      const claimsResp = await getMyClaims(currencyId || 0);
      setClaims(claimsResp.payload || []);

      const [ratesResp, rulesResp] = await Promise.all([
        GetCompanyRates(),
        getCompanyRules(company?.id)
      ]);

      const rulesCurrency = rulesResp?.payload?.currency;
      setBaseCurrency(rulesCurrency);

      const merged: CompanyCurrencyRate[] = [];
      if (rulesCurrency) {
        const baseRate: CompanyCurrencyRate = {
          id: 0,
          // @ts-expect-error existing model fields alignment
          baseCurrency: rulesCurrency,
          baseCurrencyId: rulesCurrency.id,
          foreignCurrency: rulesCurrency,
          foreignCurrencyId: 0,
          company: company,
          companyId: company?.id,
          rate: 0,
          createdOn: new Date(),
          isArchived: false
        };
        merged.push(baseRate);
      }
      if (ratesResp?.payload) merged.push(...ratesResp.payload);
      setCurrencyOptions(merged);
    } catch (e) {
      console.error('Failed loading data', e);
      showToast(t('Failed to load data'), 'error');
    } finally {
      setLoading(false);
    }
  }, [company, t, showToast]);

  useEffect(() => {
    loadData(selectedCurrency?.id || 0);
  }, [loadData, selectedCurrency]);

  // Resolve the employee's own timezone once, then re-anchor the default month to it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getUserInfo();
        if (cancelled || (res.errors && res.errors.length)) return;
        const tz = res.payload?.timeZone;
        if (tz) setProfileTimeZone(tz);
      } catch {
        // Keep the browser-zone fallback; the list must still render.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!profileTimeZone || monthPickedByUser.current) return;
    const anchor = currentMonthDate(profileTimeZone);
    setYearMonth(prev =>
      (prev.getFullYear() === anchor.getFullYear() && prev.getMonth() === anchor.getMonth()) ? prev : anchor);
  }, [profileTimeZone]);

  // Filtering
  const filteredClaims = useMemo(() => {
    return claims.filter(c => {
      const statusOk = statusFilter === 'All' || c.status === statusFilter;
      const bucket = monthOfClaim(c.requestDate as unknown as string | Date, profileTimeZone);
      const dateOk = !!bucket && bucket.year === yearMonth.getFullYear() && bucket.month === yearMonth.getMonth();
      return statusOk && dateOk;
    });
  }, [claims, statusFilter, yearMonth, profileTimeZone]);

  const totalPages = Math.max(1, Math.ceil(filteredClaims.length / PAGE_SIZE));
  const pagedClaims = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredClaims.slice(start, start + PAGE_SIZE);
  }, [filteredClaims, page]);

  // Derived summary metrics
  const summary = useMemo(() => {
    const initial = { total: 0, approved: 0, pending: 0, rejected: 0 };
    return filteredClaims.reduce((acc, c) => {
      const amt = parseFloat(c.amount || '0') || 0;
      acc.total += amt;
      if (c.status === UserClaimStatus.Approved) acc.approved += amt;
      else if (c.status === UserClaimStatus.Pending) acc.pending += amt;
      else if (c.status === UserClaimStatus.Rejected) acc.rejected += amt;
      return acc;
    }, initial);
  }, [filteredClaims]);

  const resetFilters = () => {
    setStatusFilter('All');
    monthPickedByUser.current = false;
    setYearMonth(currentMonthDate(profileTimeZone));
  };

  // Form helpers
  const validateForm = (): boolean => {
    const errors: { amount?: string; claimId?: string } = {};
    if (!form.claimId) errors.claimId = t('Claim type required');
    if (!form.amount || parseFloat(form.amount) <= 0) errors.amount = t('Enter a valid amount');
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * The user-facing reason a submit was refused. The axios interceptor rejects with the parsed
   * `Result` body, so the policy reason lives at `errors[0].description` — including the remaining
   * budget the server already computed. Known refusals are re-rendered from localized keys (the
   * server text is English-only); anything else is shown verbatim so no reason is ever dropped.
   *
   * CO-569: read through `getApiErrorMessage` rather than `extractApiError`, which saw only a
   * literal `errors` array on the rejection. That missed the wrapper shapes (`__apiErrors`,
   * `response.data.errors`), the bodiless 403 and the offline case, all of which fell to the
   * generic fallback. The refusal table still runs on whatever text comes back, so a matched
   * refusal is still localized and an unmatched one is still shown verbatim.
   */
  const submitErrorMessage = (e: unknown): string => {
    const description = getApiErrorMessage(e, t, t('Submission failed'));
    for (const refusal of SERVER_REFUSALS) {
      const match = refusal.re.exec(description.trim());
      if (match) return t(refusal.key, { [refusal.param]: match[1].trim() });
    }
    return description;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      const input: UserClaimInput = {
        id: 0,
        amount: form.amount,
        companyClaimTypeId: Number(form.claimId),
        status: UserClaimStatus.Pending,
        comments: form.comments
      };
      const resp = await addUserClaim(input);
      // A refusal normally arrives as a rejected promise (HTTP 400 → interceptor), but never
      // announce "Claim submitted" over a body that carries errors.
      if (resp.errors && resp.errors.length) {
        showToast(submitErrorMessage(resp), 'error');
        return;
      }
      if (resp.status === 200 && resp.payload?.id && form.attachments.length) {
        await addUserClaimAttachments(resp.payload.id, form.attachments);
      }
      showToast(t('Claim submitted'), 'success');
      setOpenClaimDialog(false);
      setForm({ claimId: '', amount: '', comments: '', attachments: [] });
      await loadData(selectedCurrency?.id || 0);
    } catch (e: any) {
      console.error(e);
      showToast(submitErrorMessage(e), 'error');
    } finally {
      setLoading(false);
    }
  };

  const statusBadgeVariant = (status: string) => {
    switch (status) {
      case UserClaimStatus.Approved: return 'success' as const;
      case UserClaimStatus.Rejected: return 'error' as const;
      case UserClaimStatus.Pending: return 'warning' as const;
      default: return 'neutral' as const;
    }
  };

  return (
    <PageLayout
      title={t('My Claims')}
      icon={Receipt}
      accentColor="emerald"
      maxWidth="1600px"
      headerActions={
        <Button
          variant="outline"
          {...write.buttonProps}
          onClick={() => { setOpenClaimDialog(true); loadClaimTypes(); }}
          data-testid="add-claim"
        >
          {t('REQUEST CLAIM')}
        </Button>
      }
    >
    <div className="relative flex flex-col gap-4">
      <RestrictedWriteNotice />
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="outline"
          iconName={showFilters ? 'Minus' : 'Plus'}
          onClick={() => setShowFilters(s => !s)}
        >
          {t('Filter')}
        </Button>
        <CurrencyDropdown
          value={selectedCurrency}
          options={currencyOptions}
          onOptionChange={(option: any) => setSelectedCurrency(option)}
        />
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="transition-all duration-200 rounded-xl border border-border bg-card p-4 flex flex-col gap-4">
          <div className="flex flex-col gap-2" data-testid="claim-status-filter">
            <span className="text-sm font-semibold text-foreground">{t('Status')}</span>
            <div className="flex items-center gap-4 flex-wrap">
              {[
                { value: 'All', label: 'All' },
                { value: UserClaimStatus.Pending, label: 'Pending' },
                { value: UserClaimStatus.Rejected, label: 'Rejected' },
                { value: UserClaimStatus.Approved, label: 'Approved' },
              ].map(opt => (
                <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="statusFilter"
                    value={opt.value}
                    checked={statusFilter === opt.value}
                    onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                    className="text-primary focus:ring-ring"
                  />
                  <span className="text-sm text-foreground">{t(opt.label)}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <YearMonthPicker
              date={yearMonth}
              onChange={(d: Date) => { monthPickedByUser.current = true; setYearMonth(d); setPage(1); }}
              fromMonth={new Date(new Date().getFullYear() - 8, 0, 1)}
              toMonth={new Date(new Date().getFullYear() + 1, 11, 1)}
              isRtl={false}
            />
            <Button variant="ghost" size="sm" onClick={resetFilters}>{t('Reset')}</Button>
          </div>
        </div>
      )}

      {/* Summary Bar */}
      <div className="flex flex-end flex-wrap gap-2 pt-2">
        <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-sm font-medium text-primary">
          {t('Total')}: {formatNumber(summary.total)}
        </span>
        <span className="inline-flex items-center rounded-full border border-green-500/30 bg-green-50 px-3 py-1 text-sm font-medium text-green-700">
          {t('Approved')}: {formatNumber(summary.approved)}
        </span>
        <span className="inline-flex items-center rounded-full border border-amber-500/30 bg-amber-50 px-3 py-1 text-sm font-medium text-amber-700">
          {t('Pending')}: {formatNumber(summary.pending)}
        </span>
        <span className="inline-flex items-center rounded-full border border-red-500/30 bg-red-50 px-3 py-1 text-sm font-medium text-red-700">
          {t('Rejected')}: {formatNumber(summary.rejected)}
        </span>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="claims-list">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('Claim Id')}</TableHead>
              <TableHead>{t('Date Requested')}</TableHead>
              <TableHead>{t('Status')}</TableHead>
              <TableHead className="text-end">{t('Amount')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagedClaims.map(row => (
              <TableRow key={row.id}>
                <TableCell>
                  <Link to={`/payroll/claims/${row.id}`} className="font-semibold underline text-primary hover:text-primary/80">
                    {row.id}
                  </Link>
                </TableCell>
                <TableCell>{formatDate(new Date(row.requestDate))}</TableCell>
                <TableCell>
                  <StatusBadge variant={statusBadgeVariant(row.status)} size="sm">
                    {t(row.status)}
                  </StatusBadge>
                </TableCell>
                <TableCell className="text-end">{formatNumber(parseFloat(row.amount))} {row.currency?.currencyCode || ''}</TableCell>
              </TableRow>
            ))}
            {!pagedClaims.length && (
              <TableRow>
                <TableCell colSpan={4}>
                  <div className="flex items-center justify-center py-8">
                    <span className="text-sm text-muted-foreground">{t('No claims found')}</span>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex justify-end p-3">
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={(p) => setPage(p)}
        />
      </div>

      {/* Claim Dialog */}
      <Dialog open={openClaimDialog} onOpenChange={(open) => { if (!open) setOpenClaimDialog(false); }}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>{t('REQUEST CLAIM')}</DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-4">
            <Select
              searchable
              options={claimTypes.map(c => ({ value: c.id.toString(), label: c.name }))}
              value={form.claimId}
              onChange={(val: any) => setForm(f => ({ ...f, claimId: val?.toString() || '' }))}
              label={t('Claim')}
              error={formErrors.claimId}
              placeholder={t('Select')}
            />
            <Input
              label={t('Amount')}
              type="number"
              value={form.amount}
              onChange={(e: any) => setForm(f => ({ ...f, amount: e.target.value }))}
              error={formErrors.amount}
            />
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t('Comments')}</label>
              <textarea
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-h-[60px]"
                value={form.comments}
                onChange={(e) => setForm(f => ({ ...f, comments: e.target.value }))}
                rows={3}
              />
            </div>
            <Button
              variant="outline"
              className="self-start"
              onClick={() => {
                const fileInput = document.getElementById('claim-file-input');
                if (fileInput) fileInput.click();
              }}
            >
              <AppIcon name="Paperclip" size={16} className="me-2" />
              {form.attachments.length
                ? t('Files selected', { count: form.attachments.length, defaultValue: 'Files selected: {{count}}' })
                : t('Attach files')}
            </Button>
            <input
              id="claim-file-input"
              hidden
              type="file"
              multiple
              onChange={(e) => {
                const files = e.target.files ? Array.from(e.target.files) : [];
                const oversized = files.filter((f) => !validateFileSize(f, 'claimAttachmentMaxSizeMB').valid);
                if (oversized.length > 0) {
                  const { maxMB } = validateFileSize(oversized[0], 'claimAttachmentMaxSizeMB');
                  showToast(t('fileSizeExceedsLimit', { name: oversized[0].name, maxMB }), 'error');
                  e.target.value = '';
                  return;
                }
                setForm(f => ({ ...f, attachments: files }));
              }}
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenClaimDialog(false)}>
              {t('Cancel')}
            </Button>
            <Button variant="default" loading={loading} {...write.buttonProps} onClick={handleSubmit}>
              {t('REQUEST')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/55">
          <Spinner size="lg" />
        </div>
      )}

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </div>
    </PageLayout>
  );
};

export default MyClaims;
